import { useField } from 'formik';
import React, { ChangeEvent } from 'react';

import { InputBaseProps } from '@mui/material';

import { BaseTextField, BaseTextFieldProps } from '../BaseTextField';

export type TextInputRawProps = InputBaseProps & {
  name: string;
  label?: React.ReactNode;
  trim?: boolean;
  value: string;
  setValue: (value: string) => void;
  inputClasses?: BaseTextFieldProps['inputClasses'];
} & Pick<BaseTextFieldProps, 'mixHelperTextAndError' | 'errorHelperText' | 'helperText' | 'required'>;

export const TextInputRaw: React.VFC<TextInputRawProps> = ({ error, errorHelperText, mixHelperTextAndError, helperText, className, trim, value, setValue, onChange, onBlur, label, inputClasses, ...props }) => {
  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (trim) {
      setValue(value.trim());
    }
    onBlur?.(e);
  }, [ onBlur, value, trim ]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BaseTextField
      value={value ?? ''}
      onChange={onChange}
      label={label}
      error={error}
      helperText={helperText}
      className={className}
      fullWidth={props.fullWidth}
      placeholder={props.placeholder}
      required={props.required}
      errorHelperText={errorHelperText}
      mixHelperTextAndError={mixHelperTextAndError}
      inputClasses={inputClasses}
      slotProps={{
        input: {
          ...props,
          onBlur: handleBlur,
        }
      }}
    />
  );
};

export type TextInputExtendedProps = Omit<TextInputRawProps, 'value' | 'setValue'> & {
  // matching the formik validate function definition
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validate?: (value: any) => undefined | string | Promise<any>;
  errorTextFirst?: boolean;
  maxCharacters?: number;
  onCharactersLimit?: () => void;
};

export const TextInput: React.VFC<TextInputExtendedProps> = (props) => {
  const [ field, meta, helpers ] = useField({
    name: props.name,
    validate: props.validate,
  });

  // const helperText = props.errorTextFirst
  //   ? (meta.touched && meta.error ? meta.error : undefined) || propsHelperText
  //   : propsHelperText || (meta.touched && meta.error ? meta.error : undefined);

  const handleChange = React.useMemo(() => {
    const maxCharacters = props.maxCharacters;

    if (maxCharacters) {
      return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.target.value?.length >= maxCharacters) {
          props.onCharactersLimit?.();
          e.target.value = e.target.value?.slice(0, maxCharacters);
          return field.onChange(e);
        }
        return field.onChange(e);
      };
    }

    return field.onChange;
  }, [ props.maxCharacters ]);

  return (
    <TextInputRaw
      setValue={helpers.setValue}
      error={Boolean(meta.error && meta.touched)}
      errorHelperText={meta.touched && meta.error ? meta.error : undefined}
      minRows={props.rows}
      {...field}
      {...props}
      onChange={handleChange}
    />
  );
};
